import React from 'react';
import ReactRouterPropTypes from 'react-router-prop-types';
import { useDispatch, useSelector } from 'react-redux';
import PageContainer from 'containers/PageContainer';
import LoginView from 'views/LoginView';
import { Auth } from 'lib/Amplify';
import { setAppBusy } from 'store/actions/system';
import {
  setAccountId,
  setEmailAddress,
  setEmailVerified,
  setUser,
} from 'store/actions/user';
import useMessages from 'hooks/useMessages';
import { Redirect } from 'react-router';

const LoginContainer = ({ history }) => {
  const dispatch = useDispatch();
  const emailAddress = useSelector((state) => state.email);
  const user = useSelector((state) => state.user);
  const {
    failedLoginMessage, genericFailureMessage, clearMessage, tooManyAttemptsMessage,
  } = useMessages();

  const handleResetPassword = () => {
    dispatch(setAppBusy(true));
    clearMessage();
    Auth.forgotPassword(emailAddress)
      .then(() => {
        history.push('/reset_password');
      })
      .catch(() => {
        genericFailureMessage();
      })
      .finally(() => {
        dispatch(setAppBusy(false));
      });
  };

  const handleLoginWithOtherAccount = () => {
    clearMessage();
    dispatch(setEmailAddress(null));
    history.replace('/start');
  };

  const handleSubmit = (password) => {
    dispatch(setAppBusy(true));
    clearMessage();
    Auth.signIn(emailAddress, password)
      .then((res) => {
        dispatch(setUser(res));
        dispatch(setEmailVerified());
        clearMessage();
        if (res.attributes['custom:accountId']) {
          dispatch(setAccountId(res.attributes['custom:accountId']));
          history.replace('/overview');
        } else {
          history.replace('/find_account');
        }
      })
      .catch((err) => {
        console.log(err);
        switch (err.code) {
          case 'UserNotConfirmedException':
            history.push('/verify');
            break;

          case 'NotAuthorizedException':
            // Cognito seems to handle this wrong, the error code should be "LimitExceededException"
            if (err.message === 'Password attempts exceeded') {
              tooManyAttemptsMessage();
            } else {
              failedLoginMessage(handleLoginWithOtherAccount, handleResetPassword);
            }
            break;

          case 'LimitExceededException':
            tooManyAttemptsMessage();
            break;

          default:
            genericFailureMessage('Something went wrong, please try again');
        }
      })
      .finally(() => {
        dispatch(setAppBusy(false));
      });
  };

  if (!emailAddress) {
    return <Redirect to="/start" />;
  }

  if (user) {
    return <Redirect to="/" />;
  }

  return (
    <PageContainer hasBack>
      <LoginView
        email={emailAddress}
        onSubmit={handleSubmit}
        onForgotPasswordClick={handleResetPassword}
      />
    </PageContainer>
  );
};

LoginContainer.displayName = 'LoginContainer';

LoginContainer.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
};

export default LoginContainer;
